import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CategoryScroller from '../Marketplace/CategoryScroller';
import TagScroller from './TagScroller';
import axios from 'axios'; 
import { ecommerceServer } from "../../server";
import { toggleTopLevelDropdown } from "../../state/uiSlice";
import { useDispatch } from "react-redux";
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const MarketplaceDropdown = ({ categoriesData, setDropDown }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef();
  const [selectedCategory, setSelectedCategory] = useState(categoriesData[0]);
  const [tags, setTags] = useState([]);
  const [featuredData, setFeaturedData] = useState({ products: [], shop: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [productRailState, setProductRailState] = useState({ hasOverflow: false, canScrollLeft: false, canScrollRight: false });
  const [shopRailState, setShopRailState] = useState({ hasOverflow: false, canScrollLeft: false, canScrollRight: false });
  const dispatch = useDispatch();
  const productRailRef = useRef(null);
  const shopRailRef = useRef(null);

  const updateRailState = useCallback((railRef, setRailState) => {
    const rail = railRef.current;
    if (!rail) {
      setRailState({ hasOverflow: false, canScrollLeft: false, canScrollRight: false });
      return;
    }

    const hasOverflow = rail.scrollWidth > rail.clientWidth + 2;
    setRailState({
      hasOverflow,
      canScrollLeft: hasOverflow && rail.scrollLeft > 2,
      canScrollRight: hasOverflow && rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 2,
    });
  }, []);

  const scrollRail = useCallback((railRef, direction) => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }

    rail.scrollBy({
      left: direction === 'next' ? Math.max(rail.clientWidth * 0.72, 180) : -Math.max(rail.clientWidth * 0.72, 180),
      behavior: 'smooth',
    });

    window.setTimeout(() => {
      updateRailState(productRailRef, setProductRailState);
      updateRailState(shopRailRef, setShopRailState);
    }, 320);
  }, [updateRailState]);

  const updateAllRailStates = useCallback(() => {
    updateRailState(productRailRef, setProductRailState);
    updateRailState(shopRailRef, setShopRailState);
  }, [updateRailState]);

  useEffect(() => {
    axios.get(`${ecommerceServer}/tags/tags/approved`)
      .then((res) => setTags(res.data))
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    updateAllRailStates();
    const overflowTimer = window.setTimeout(updateAllRailStates, 140);

    window.addEventListener('resize', updateAllRailStates);

    return () => {
      window.clearTimeout(overflowTimer);
      window.removeEventListener('resize', updateAllRailStates);
    };
  }, [featuredData, updateAllRailStates]);

  // Fetch featured products and shop when the selected category changes
  useEffect(() => {
    const fetchFeaturedData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${ecommerceServer}/product/featured/${encodeURIComponent(selectedCategory.title)}`,
          { withCredentials: true }
        );
        setFeaturedData(response.data);
      } catch (error) {
        console.error('Error fetching featured data:', error);
        setError('Failed to load featured data.');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedData();
  }, [selectedCategory]);

  // Handle category selection from CategoryScroller
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  // Handle subcategory navigation
  const handleSubCategoryClick = (subCategory) => {
    navigate(`/products/${selectedCategory.title}/${subCategory.title}`);
    dispatch(toggleTopLevelDropdown());
    window.location.reload();
  };

  // Handle tag click
  const handleTagClick = (tag) => {
    navigate(`/products/tag/${encodeURIComponent(tag.name)}`);
    dispatch(toggleTopLevelDropdown());
  };

  return (
    <div ref={dropdownRef} className="marketplace-dropdown-container">
      <CategoryScroller
        categoriesData={categoriesData}
        onCategorySelect={handleCategorySelect}
        currentCategory={selectedCategory?.title}
        layout="horizontal"
      />

      {/* Subcategories Section */}
      <div className="marketplace-dropdown-subcategories">
        {selectedCategory.subCategories.map((subCategory) => (
          <div
            key={subCategory.id}
            className="marketplace-dropdown-subcategory"
            onClick={() => handleSubCategoryClick(subCategory)}
          >
            <span className="marketplace-dropdown-subcategory-text">{subCategory.title}</span>
          </div>
        ))}
      </div>

      

      <div className="marketplace-dropdown-inner">
        {/* Featured Products and Shop Section */}
        <div className="marketplace-dropdown-section">
          {loading && <div>Loading featured data...</div>}
          {error && <div>{error}</div>}
          {!loading && !error && (
            <>
            <div className="marketplace-dropdown-feature-products-shell">
              {productRailState.hasOverflow && (
                <button
                  type="button"
                  className="marketplace-dropdown-rail-arrow marketplace-dropdown-rail-arrow--prev"
                  onClick={() => scrollRail(productRailRef, 'previous')}
                  disabled={!productRailState.canScrollLeft}
                  aria-label="Previous featured product"
                >
                  <FaChevronLeft aria-hidden="true" />
                </button>
              )}
            <div
              className="marketplace-dropdown-feature-products"
              ref={productRailRef}
              onScroll={() => updateRailState(productRailRef, setProductRailState)}
            >
              {/* Featured Products */}
              {featuredData.products.map((product, index) => (
                <div
                  key={index}
                  className="marketplace-dropdown-product"
                  onClick={() => {
                    navigate(`/product/${product._id}`);
                    dispatch(toggleTopLevelDropdown());
                  }}
                >
                  <img
                    src={product.images?.[0]?.url || 'default-image-url'}
                    alt={product.name}
                    className="marketplace-dropdown-product-image"
                  />
                  <div>
                    <span className="marketplace-dropdown-product-title">{product.name}</span>
                    <div className="marketplace-dropdown-product-shop">{product.shop?.name}</div>
                  </div>
                </div>
              ))}
              </div>
              {productRailState.hasOverflow && (
                <button
                  type="button"
                  className="marketplace-dropdown-rail-arrow marketplace-dropdown-rail-arrow--next"
                  onClick={() => scrollRail(productRailRef, 'next')}
                  disabled={!productRailState.canScrollRight}
                  aria-label="Next featured product"
                >
                  <FaChevronRight aria-hidden="true" />
                </button>
              )}
              </div>

              {/* Featured Shop */}
              {featuredData.shop && (
                <div className="marketplace-dropdown-featured-shop-shell">
                {shopRailState.hasOverflow && (
                  <button
                    type="button"
                    className="marketplace-dropdown-rail-arrow marketplace-dropdown-rail-arrow--prev"
                    onClick={() => scrollRail(shopRailRef, 'previous')}
                    disabled={!shopRailState.canScrollLeft}
                    aria-label="Previous featured shop"
                  >
                    <FaChevronLeft aria-hidden="true" />
                  </button>
                )}
                <div
                  className="marketplace-dropdown-featured-shop-rail"
                  ref={shopRailRef}
                  onScroll={() => updateRailState(shopRailRef, setShopRailState)}
                >
                <div
                  className="marketplace-dropdown-featured-shop"
                  onClick={() => {
                    navigate(`/shop/${featuredData.shop?.handle}`);
                    dispatch(toggleTopLevelDropdown());
                  }}
                >
                  <div className="marketplace-dropdown-featured-shop-inner">
                    <img
                      src={featuredData.shop.banner?.url || 'default-shop-image-url'}
                      alt={featuredData.shop.name}
                      className="marketplace-dropdown-featured-shop-image"
                    />
                    <div className="marketplace-dropdown-featured-shop-overlay">
                      <span className="marketplace-dropdown-featured-shop-text">{featuredData.shop.name}</span>
                    </div>
                  </div>
                </div>
                </div>
                {shopRailState.hasOverflow && (
                  <button
                    type="button"
                    className="marketplace-dropdown-rail-arrow marketplace-dropdown-rail-arrow--next"
                    onClick={() => scrollRail(shopRailRef, 'next')}
                    disabled={!shopRailState.canScrollRight}
                    aria-label="Next featured shop"
                  >
                    <FaChevronRight aria-hidden="true" />
                  </button>
                )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketplaceDropdown;
